import { StepInfo } from "@/components/FormStepper/FormStepper"
import React, { ReactNode, createContext, useContext, useState } from "react"
import type { UseFormReturn } from "react-hook-form"

interface FormStepperContextParams<T = any> {
    isFirstStep?: boolean
    isLastStep?: boolean
    actualStep: number
    steps?: StepInfo[]
    toStep: (nextStep: number) => void
    handleOnInOutCallbacks: (callbacks: StepInfo["onIn"] | StepInfo["onOut"]) => Promise<boolean>
    toNextStep?: () => void
    toPrevStep?: () => void
    setSteps?: (value: StepInfo[]) => void
    setReactHookForm?: (value: UseFormReturn<T, any, undefined>) => void
    validate?: () => Promise<boolean>
}
interface FormStepperContextProviderProps<T> {
    children: ReactNode
    stepStartAt?: number
}

export const FormStepperContext: any = createContext({} as FormStepperContextParams)

export function FormStepperContextProvider<T>({ children, stepStartAt = 0 }: FormStepperContextProviderProps<T>) {
    const [actualStep, setActualStep] = useState(stepStartAt)
    const [hookForm, setHookForm] = useState<UseFormReturn<T, any, undefined>>({} as UseFormReturn<T, any, undefined>)
    const [steps, setSteps] = useState<StepInfo[]>([{ id: "0", components: [], name: "", title: "", description: "" }])

    const handleOnInOutCallbacks = async (callbacks: StepInfo["onIn"] | StepInfo["onOut"]) => {
        if (!callbacks) return true

        const payload = { actualStep: actualStep, steps, ...hookForm }
        const callbacksFeedback = await Promise.all(callbacks.map(async (callback) => await callback(payload)))
        return callbacksFeedback.every((value) => value)
    }

    const handleChangeToStep = async (nextStep: number) => {
        if (nextStep !== actualStep) {
            const result = await handleOnInOutCallbacks(steps[actualStep]?.onOut)

            if (result || nextStep < actualStep) {
                setActualStep(nextStep)
                await handleOnInOutCallbacks(steps[nextStep]?.onIn)
            }
        }
    }

    const handleValidateCurrentStep = async () => await handleOnInOutCallbacks(steps[actualStep]?.onOut)

    const handleChangeToPrevStep = () => handleChangeToStep(actualStep - 1)

    const handleChangeToNextStep = () => handleChangeToStep(actualStep + 1)

    const setReactHookForm = (value: any) => {
        setHookForm(value)
    }

    return (
        <FormStepperContext.Provider
            value={{
                steps: steps,
                isLastStep: actualStep === steps.length - 1,
                isFirstStep: actualStep === 0,
                actualStep,
                toStep: handleChangeToStep,
                toNextStep: handleChangeToNextStep,
                toPrevStep: handleChangeToPrevStep,
                setReactHookForm,
                handleOnInOutCallbacks,
                validate: handleValidateCurrentStep,
                setSteps,
            }}
        >
            {children}
        </FormStepperContext.Provider>
    )
}

export function useFormStepper<T = any>() {
    return useContext<FormStepperContextParams<T>>(FormStepperContext)
}
